/**
 * Decision logic for keeping a stream alive.
 *
 * The browser stops playback in several ways that look nothing like an error:
 * a device or route change pauses the element, a truncated range response
 * leaves a hole the element will never fetch, an HTTP connection dies while
 * `readyState` still claims it holds enough data. Recovering means deciding,
 * from a few numbers, which of those happened and what to do about it.
 *
 * That decision is pure and lives here. useAudioElement keeps the DOM wiring.
 */

/** How close playback has to get to the end of its buffered island before a
 * hole ahead of it is treated as fatal. One second is enough to act before the
 * audio runs dry, without firing on a range that is still being extended. */
export const BUFFER_HOLE_LOOKAHEAD_S = 1

/** The browser can pause mid-track with no error. Re-issue play to recover, but
 * bail if it re-pauses repeatedly in a short window so a genuinely stuck element
 * doesn't thrash. */
export const AUTO_RESUME_WINDOW_MS = 1500
export const AUTO_RESUME_MAX = 3

/** A stalled stream is reloaded at most this many times before the error is
 * allowed to surface, so a dead stream doesn't retry forever. */
export const MAX_RECOVERY_ATTEMPTS = 2

/** `recovering` suppresses error handling and auto-resume, so it must never
 * latch on — clear it on a timer in case the reload never produces `playing`. */
export const RECOVERY_TIMEOUT_MS = 15_000

/** How often the stall watchdog samples playback position. */
export const STALL_TICK_MS = 5_000

/** Watchdog ticks without progress before each successive stall reload. The
 * reload that follows a dead connection usually lands while the network is
 * still down (a VPN reconnecting, a dropped Wi-Fi link), so each attempt waits
 * longer than the last. Its length is the stall reload budget: once spent, the
 * stream is given up on rather than retried forever. */
export const STALL_RELOAD_TICKS = [2, 4, 8]

/** HTMLMediaElement.NETWORK_IDLE, without needing an element to read it from. */
const NETWORK_IDLE = 1

/** The parts of an HTMLAudioElement this module reads. */
export interface AudioReading {
  currentTime: number
  readyState: number
  networkState: number
  buffered: { length: number; start: (i: number) => number; end: (i: number) => number }
}

/**
 * Chrome reports NETWORK_IDLE once it believes the resource is fully loaded,
 * which it does as soon as it holds the head and the tail — even when a
 * truncated range response left a hole in between. Nothing will ever fetch that
 * hole, so playback runs into it and dies with the network quiet and readyState
 * still claiming HAVE_ENOUGH_DATA.
 */
export function hasBufferHoleAhead(
  audio: AudioReading,
  lookaheadSeconds = BUFFER_HOLE_LOOKAHEAD_S
): boolean {
  if (audio.networkState !== NETWORK_IDLE) return false
  if (audio.readyState < 1) return false

  const { buffered, currentTime } = audio
  if (buffered.length < 2) return false

  for (let i = 0; i < buffered.length; i++) {
    if (currentTime < buffered.start(i) || currentTime > buffered.end(i)) continue
    // Ranges are ordered, so a range after this one is data the element can
    // never reach on its own.
    return i < buffered.length - 1 && buffered.end(i) - currentTime <= lookaheadSeconds
  }
  return false
}

/**
 * Strict equality misses "slow drip" stalls where tiny amounts of data trickle
 * in, advancing time by fractions of a second — enough to reset a naive
 * watchdog but not enough to play. Time moving backwards is a seek, never a
 * stall.
 */
export function isEffectivelyStalled(previousTime: number, currentTime: number): boolean {
  const progress = currentTime - previousTime
  return currentTime > 0 && progress >= 0 && progress < 1
}

/**
 * `resume` — re-issue play(); the element paused but playback is otherwise healthy.
 * `reload` — play() isn't helping; force a fresh connection.
 * `concede` — out of options; let the UI show a stop.
 *
 * A union rather than an enum: the client compiles with `erasableSyntaxOnly`,
 * which rules out anything that emits runtime code.
 */
export type AutoResumeAction = 'resume' | 'reload' | 'concede'

/**
 * Budget for browser-initiated pauses. Conceding flips `isPlaying` false, which
 * parks the stall watchdog and takes the last recovery path with it, so never
 * concede while a reload is still available.
 */
export function planAutoResume(options: {
  count: number
  lastAt: number
  now: number
  recoveryAttempts: number
}): { count: number; action: AutoResumeAction } {
  const { count, lastAt, now, recoveryAttempts } = options
  const withinWindow = now - lastAt < AUTO_RESUME_WINDOW_MS
  const nextCount = withinWindow ? count + 1 : 1

  if (nextCount <= AUTO_RESUME_MAX) return { count: nextCount, action: 'resume' }
  if (recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
    return { count: nextCount, action: 'reload' }
  }
  return { count: nextCount, action: 'concede' }
}

/**
 * Where the stall watchdog last saw playback. The baseline is per-stream:
 * `currentTime` is only comparable against a previous reading from the same
 * channel playing the same src.
 */
export interface StallBaseline {
  channel: string | null
  src: string | null
  time: number
  ticks: number
}

export const freshBaseline = (): StallBaseline => ({
  channel: null,
  src: null,
  time: -1,
  ticks: 0,
})

/**
 * `healthy` — playback is progressing, or the element is parked.
 * `rebaselined` — a channel flip or new src; this reading is not comparable.
 * `hole-ahead` — heading into a hole nothing will fill; reload now.
 * `stalling` — first tick without progress; worth logging, not yet a reload.
 * `stalled` — still not moving; reload the stream.
 * `exhausted` — still not moving and the reload budget is spent; give up.
 * `recovered` — moving again after a stall.
 */
export type StallVerdict =
  | 'healthy'
  | 'rebaselined'
  | 'hole-ahead'
  | 'stalling'
  | 'stalled'
  | 'exhausted'
  | 'recovered'

export interface StallReading {
  channel: string
  src: string
  time: number
  /** Gated on the element alone: `isPlaying` can be false while the element is
   * still running (a conceded auto-resume does exactly that), and that is
   * precisely the state this watchdog exists to dig out of. */
  paused: boolean
  recovering: boolean
  holeAhead: boolean
  /** Stall reloads already spent on this stream since it last made progress. */
  reloads: number
}

export function observeStall(
  baseline: StallBaseline,
  reading: StallReading
): { baseline: StallBaseline; verdict: StallVerdict } {
  if (reading.paused) return { baseline: freshBaseline(), verdict: 'healthy' }

  // The jump back to the head of a new track would otherwise read as a stall.
  if (reading.channel !== baseline.channel || reading.src !== baseline.src) {
    return {
      baseline: { channel: reading.channel, src: reading.src, time: reading.time, ticks: 0 },
      verdict: 'rebaselined',
    }
  }

  if (!reading.recovering && reading.holeAhead) {
    return { baseline, verdict: 'hole-ahead' }
  }

  if (isEffectivelyStalled(baseline.time, reading.time)) {
    const ticks = baseline.ticks + 1
    // The last reload gets the longest wait before the stream is given up on.
    const budget = STALL_RELOAD_TICKS.length
    const threshold = STALL_RELOAD_TICKS[Math.min(reading.reloads, budget - 1)] ?? 2
    const overdue = ticks >= threshold
    return {
      baseline: { ...baseline, ticks },
      // Gentler approaches (pause/play, seek) don't fix dead HTTP connections.
      verdict: !overdue ? 'stalling' : reading.reloads < budget ? 'stalled' : 'exhausted',
    }
  }

  return {
    baseline: { ...baseline, time: reading.time, ticks: 0 },
    verdict: baseline.ticks > 0 ? 'recovered' : 'healthy',
  }
}

/** Longest a track has to be listened to before it scrobbles, in seconds.
 * Past four minutes, half of a long track is far more than anyone needs to
 * hear to have listened to it. */
const SCROBBLE_CAP_S = 240

/**
 * Whether playback has passed the point at which a track counts as listened to:
 * halfway, or four minutes, whichever comes first.
 */
export function passedScrobbleMark(options: { currentTime: number; duration: number }): boolean {
  const { currentTime, duration } = options
  if (duration <= 0) return false
  return currentTime >= Math.min(duration * 0.5, SCROBBLE_CAP_S)
}

/** The parts of the inactive channel's element that decide whether it can take over. */
export interface PreloadedChannel {
  preloadedSrc: string | null
  readyState: number
}

/**
 * Whether the next track can be started by flipping channels instead of going
 * through the React state -> effect path.
 *
 * The inactive element has to already hold the exact stream that is coming up,
 * with enough decoded to play (readyState >= HAVE_CURRENT_DATA). Interstitials
 * are excluded on both sides: they are synthesized on demand rather than
 * preloaded, so neither the outgoing nor the incoming one is ever buffered
 * ahead on the other channel.
 */
export function canSwapGaplessly(options: {
  nextSrc: string
  nextIsInterstitial: boolean
  currentIsInterstitial: boolean
  inactive: PreloadedChannel
}): boolean {
  const { nextSrc, nextIsInterstitial, currentIsInterstitial, inactive } = options
  if (nextIsInterstitial || currentIsInterstitial) return false
  return inactive.preloadedSrc === nextSrc && inactive.readyState >= 2
}
